import { Constants } from '../../shared/Constants';

const ITEM_LABEL_SELECTOR = 'span.gm-toolbar-item-label, span.gm-group-btn-label';
const ITEM_LABEL_CLASS = 'gm-toolbar-item-label';

function normalizeBootstrapIcon(icon) {
    if (!icon) return '';
    const raw = String(icon).trim();
    if (raw.startsWith('bi ')) return raw;
    if (raw.startsWith('bi-')) return `bi ${raw}`;
    return `bi bi-${raw}`;
}

/**
 * Whether a toolbar button / group / split item should be rendered.
 * `.showIf(fn)` receives the adapter and should return a truthy value to show.
 *
 * @param {{ showIf?: Function } | null | undefined} config
 * @param {*} adapter
 * @returns {boolean}
 */
export function isToolbarButtonVisible(config, adapter) {
    if (!config || typeof config.showIf !== 'function') return true;
    try {
        return Boolean(config.showIf(adapter));
    } catch (err) {
        console.warn('DataTableAdapter: toolbar showIf threw; button will remain visible.', err);
        return true;
    }
}

/**
 * Runtime controller for a primary toolbar button DOM node.
 * Passed as the second argument to ToolbarBuilder click handlers:
 * `.onClick(adapter, toolbarBtn)` / `.onProcess(adapter, toolbarBtn)`.
 */
export function createToolbarButtonController(btnConfig, $btn) {
    const applyVisual = () => {
        if (!$btn || !$btn.length || !btnConfig) return;

        const title = btnConfig.title ?? '';
        const tooltip = title || btnConfig.key || '';
        $btn.attr('title', tooltip);
        if (tooltip) $btn.attr('aria-label', tooltip);

        if (btnConfig.color != null) {
            $btn.css('background-color', Constants.resolveColor(btnConfig.color));
        }

        if (btnConfig.textColor != null) {
            $btn.css('color', Constants.resolveColor(btnConfig.textColor));
        }

        const iconClass = normalizeBootstrapIcon(btnConfig.icon);
        let $icon = $btn.find('i').first();

        if (iconClass) {
            if ($icon.length) {
                $icon.attr('class', iconClass);
            } else {
                $btn.prepend($('<i></i>').attr('class', iconClass));
                $icon = $btn.find('i').first();
            }
        } else if ($icon.length) {
            $icon.remove();
        }

        const $label = $btn.find('span').first();
        if ($label.length) {
            if (title) $label.text(title);
            else $label.remove();
            return;
        }

        // Fallback toolbar markup: icon + text without a label span
        const iconHtml = iconClass ? `<i class="${iconClass}${title ? ' me-1' : ''}"></i>` : '';
        const extraClass = btnConfig.className || '';
        $btn
            .attr('class', `btn btn-sm btn-outline-secondary ${extraClass} gm-btn-${btnConfig.key} dt-toolbar-btn`.trim())
            .html(title ? `${iconHtml}${title}` : `${iconHtml}`);
    };

    const controller = {
        get key() {
            return btnConfig.key;
        },
        get title() {
            return btnConfig.title;
        },
        get icon() {
            return btnConfig.icon;
        },
        get color() {
            return btnConfig.color;
        },
        get textColor() {
            return btnConfig.textColor;
        },
        setTitle(title) {
            btnConfig.title = title;
            applyVisual();
            return controller;
        },
        setIcon(icon) {
            btnConfig.icon = icon;
            applyVisual();
            return controller;
        },
        setBackgroundColor(color) {
            btnConfig.color = color;
            applyVisual();
            return controller;
        },
        /** @deprecated Use setBackgroundColor */
        withBackgroundColor(color) {
            return controller.setBackgroundColor(color);
        },
        withColor(color) {
            btnConfig.textColor = color;
            if ($btn?.length) {
                $btn.css('color', Constants.resolveColor(color));
            }
            return controller;
        },
        setClass(className) {
            btnConfig.className = className;
            if ($btn?.length) {
                $btn.addClass(className);
            }
            return controller;
        },
        update(props = {}) {
            if (props.title !== undefined) btnConfig.title = props.title;
            if (props.icon !== undefined) btnConfig.icon = props.icon;
            if (props.color !== undefined) btnConfig.color = props.color;
            if (props.textColor !== undefined) btnConfig.textColor = props.textColor;
            if (props.className !== undefined) btnConfig.className = props.className;
            applyVisual();
            return controller;
        },
        apply: applyVisual
    };

    return controller;
}

/**
 * Runtime controller for group segments and split dropdown items.
 * Mutates the clicked item’s own DOM node (not the parent primary button).
 *
 * @param {object} itemConfig
 * @param {JQuery} $btn
 */
export function createToolbarItemController(itemConfig, $btn) {
    const applyVisual = () => {
        if (!$btn || !$btn.length || !itemConfig) return;

        const hasText = itemConfig.title !== undefined && itemConfig.title !== null && itemConfig.title !== '';
        const title = hasText ? itemConfig.title : '';
        const tooltip = itemConfig.title ?? itemConfig.key ?? '';
        if (tooltip) {
            $btn.attr('title', tooltip);
            $btn.attr('aria-label', tooltip);
        }

        if (itemConfig.color) {
            $btn.css('background-color', Constants.resolveColor(itemConfig.color));
        } else {
            $btn.css('background-color', '');
        }

        if (itemConfig.textColor) {
            $btn.css('color', Constants.resolveColor(itemConfig.textColor));
        } else if (itemConfig.color) {
            $btn.css('color', '#fff');
        } else {
            $btn.css('color', '');
        }

        const iconClass = normalizeBootstrapIcon(itemConfig.icon);
        const iconOnly = !hasText && itemConfig.icon;
        let $icon = $btn.find('i').first();

        if (iconClass) {
            const finalIconClass = `${iconClass}${iconOnly ? '' : ' me-2'}`;
            if ($icon.length) $icon.attr('class', finalIconClass);
            else $btn.prepend($('<i></i>').attr('class', finalIconClass));
        } else if ($icon.length) {
            $icon.remove();
        }

        let $label = $btn.find(ITEM_LABEL_SELECTOR).first();
        if (hasText) {
            if ($label.length) {
                $label.text(title);
            } else {
                $label = $('<span></span>').addClass(ITEM_LABEL_CLASS).text(title);
                $btn.append($label);
            }
        } else if ($label.length) {
            $label.remove();
        }
    };

    const controller = {
        get key() { return itemConfig.key; },
        get title() { return itemConfig.title; },
        get icon() { return itemConfig.icon; },
        get color() { return itemConfig.color; },
        get textColor() { return itemConfig.textColor; },
        setTitle(title) { itemConfig.title = title; applyVisual(); return controller; },
        setIcon(icon) { itemConfig.icon = icon; applyVisual(); return controller; },
        setBackgroundColor(color) { itemConfig.color = color; applyVisual(); return controller; },
        /** @deprecated Use setBackgroundColor */
        withBackgroundColor(color) { return controller.setBackgroundColor(color); },
        withColor(color) {
            itemConfig.textColor = color;
            if ($btn?.length) {
                $btn.css('color', Constants.resolveColor(color));
            }
            return controller;
        },
        setClass(className) {
            itemConfig.className = className;
            if ($btn?.length) $btn.addClass(className);
            return controller;
        },
        update(props = {}) {
            if (props.title !== undefined) itemConfig.title = props.title;
            if (props.icon !== undefined) itemConfig.icon = props.icon;
            if (props.color !== undefined) itemConfig.color = props.color;
            if (props.textColor !== undefined) itemConfig.textColor = props.textColor;
            if (props.className !== undefined) itemConfig.className = props.className;
            applyVisual();
            return controller;
        },
        apply: applyVisual
    };

    return controller;
}

export { normalizeBootstrapIcon };
